import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Radio,
  CircularProgress,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { STAFF_USERS_QUERY } from "./api";
import {
  StaffUsersForAssignmentQuery,
  StaffUsersForAssignmentQueryVariables,
} from "generated-graphql/graphql";

interface AssignDialogProps {
  open: boolean;
  onClose: () => void;
  onAssign: (userId: string) => void;
  title: string;
  selectedCount: number;
  isLoading?: boolean;
  /** Optional warning message to display (e.g., for virtual events) */
  warningMessage?: string;
}

export function AssignDialog({
  open,
  onClose,
  onAssign,
  title,
  selectedCount,
  isLoading = false,
  warningMessage,
}: AssignDialogProps) {
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data, loading } = useQuery<
    StaffUsersForAssignmentQuery,
    StaffUsersForAssignmentQueryVariables
  >(STAFF_USERS_QUERY, {
    variables: { search: search || null },
    skip: !open,
  });

  const handleAssign = () => {
    if (selectedUserId) {
      onAssign(selectedUserId);
    }
  };

  const handleClose = () => {
    setSearch("");
    setSelectedUserId(null);
    onClose();
  };

  const users = data?.staffUsers?.items ?? [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {title} ({selectedCount} submission{selectedCount !== 1 ? "s" : ""})
      </DialogTitle>
      <DialogContent>
        {warningMessage && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {warningMessage}
          </Alert>
        )}
        <TextField
          autoFocus
          margin="dense"
          label="Search staff"
          fullWidth
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No staff members found
          </Typography>
        ) : (
          <List sx={{ maxHeight: 300, overflow: "auto" }}>
            {users.map((user) => (
              <ListItem key={user.id} disablePadding>
                <ListItemButton
                  onClick={() => setSelectedUserId(user.id)}
                  selected={selectedUserId === user.id}
                >
                  <Radio
                    checked={selectedUserId === user.id}
                    value={user.id}
                    sx={{ mr: 1 }}
                  />
                  <ListItemText
                    primary={
                      user.person
                        ? `${user.person.first} ${user.person.last}`
                        : user.email
                    }
                    secondary={user.email}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={!selectedUserId || isLoading}
        >
          {isLoading ? <CircularProgress size={20} /> : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
